import { gql, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import {
  CollectionStatus,
  CurrentUserFragmentDoc,
  EventDetailCollectionBoxFragmentDoc,
  EventDetailFragmentDoc,
  EventDetailQuery,
  EventDetailQueryVariables,
  EventPreSaleBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useIsInside, useLocationWatcher } from '@/core/native/geolocation'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError } = useMessage()
  const { router } = useRouter<{ eventId: string; tab?: 'presale' | 'store' }>()
  const { userClaims, user: currentUser } = useCurrentUser()
  const { data, loading, fetchMore } = useQuery<
    EventDetailQuery,
    EventDetailQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.eventId,
      filter: CollectionStatus.Published,
    },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })

  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])

  const { isInside } = useIsInside({
    locations: [data?.event?.location ?? null, currentLocation],
    radius: 1000,
  })

  return {
    state: {
      currentUser,
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      loading,
      event: data?.event ?? null,
      tab: router.query.tab === 'store' ? 1 : 0,
      collections: data?.event?.eventCollections ?? null,
      hasNextPage: !!data?.event?.eventCollections.pageInfo.hasNextPage,
      hasPresalesNextPage: !!data?.event?.preSales?.pageInfo.hasNextPage,
      isInside,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
      userCartCount: data?.userCartCount ?? 0,
      // 先行販売チケットのデータ
      preSale: data?.event?.preSales ?? null,
    },
    actions: {
      async onFetchMore() {
        if (!data?.event.eventCollections.pageInfo.hasNextPage) return

        await fetchMore({
          variables: {
            id: router.query.eventId,
            eventCollectionPageInput: {
              cursor: data?.event.eventCollections.pageInfo.endCursor,
            },
          },
        })
      },
      async onFetchPreSalesMore() {
        if (!data?.event?.preSales?.pageInfo.hasNextPage) return

        await fetchMore({
          variables: {
            id: router.query.eventId,
            eventPreSalePageInput: {
              cursor: data?.event?.preSales?.pageInfo.endCursor,
            },
          },
        })
      },
      async onRequestPermission() {
        if (!data?.event?.location?.latitude) return
        requestPermission().catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query EventDetail(
    $id: ID!
    $eventCollectionPageInput: PageInput
    $eventPreSalePageInput: PageInput
    $filter: CollectionStatus
  ) {
    event(id: $id) {
      ...EventDetail
      eventCollections(pageInput: $eventCollectionPageInput, filter: $filter) {
        edges {
          node {
            ...EventDetailCollectionBox
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      preSales(pageInput: $eventPreSalePageInput) {
        edges {
          node {
            ...EventPreSaleBox
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    userCartCount
    user {
      ...CurrentUser
    }
  }
  ${EventDetailFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${EventDetailCollectionBoxFragmentDoc}
  ${EventPreSaleBoxFragmentDoc}
`
